import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Panel, Section, StateView, formatRelative, useAuth, useToast } from "@ozpanel/ui";

// Update awareness (modules/updates). Two halves, both on demand — nothing here polls upstream:
//   - the components we carry, against each upstream's latest release (checked when the button is pressed);
//   - each node's OS updates from its own apt simulation, applied one node at a time.

export interface ComponentCheck {
  name: string;
  ours: string;
  ours_commit?: string;
  latest?: string;
  latest_commit?: string;
  notes_url?: string;
  published?: string;
  status: "current" | "update" | "could_not_check";
  security: boolean;
  security_note?: string;
  error?: string;
  source: string;
}
interface ComponentsResult {
  checked_at: string;
  checked_on: string;
  components: ComponentCheck[];
}

export const componentTone = (c: ComponentCheck) =>
  c.status === "current" ? "ok" : c.status === "update" ? (c.security ? "bad" : "warn") : "neutral";
// "could not check" is never "up to date".
export const componentLabel = (c: ComponentCheck) =>
  c.status === "current" ? "up to date" : c.status === "update" ? (c.security ? "security update" : "update available") : "could not check";

export function ComponentUpdates() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["updates-components"],
    queryFn: () => client.apiFetch<{ result: ComponentsResult | null; node: string }>("/updates/components"),
  });
  const check = useMutation({
    mutationFn: () => client.apiFetch<{ result: ComponentsResult }>("/updates/components/check", { method: "POST", body: "{}" }),
    onSuccess: (r) => {
      qc.setQueryData(["updates-components"], { result: r.result, node: r.result.checked_on });
      const n = r.result.components.filter((c) => c.status === "could_not_check").length;
      notify({ kind: n ? "error" : "success", title: n ? `${n} source(s) could not be checked` : "Checked every upstream" });
    },
    onError: (e) => notify({ kind: "error", title: "Check failed", body: e instanceof Error ? e.message : String(e) }),
  });
  const res = q.data?.result;
  const cols: DataListColumn<ComponentCheck>[] = [
    { key: "name", label: "Component", sortable: true },
    { key: "ours", label: "Ours", render: (c) => <code className="font-mono text-[12px]">{c.ours}{c.ours_commit ? ` @ ${c.ours_commit.slice(0, 12)}` : ""}</code> },
    {
      key: "latest",
      label: "Latest upstream",
      value: (c) => c.latest || "",
      render: (c) =>
        c.latest ? (
          <code className="font-mono text-[12px]">{c.latest}{c.latest_commit ? ` @ ${c.latest_commit.slice(0, 12)}` : ""}</code>
        ) : (
          "—"
        ),
    },
    {
      key: "status",
      label: "Status",
      value: (c) => componentLabel(c),
      render: (c) => (
        <span className="flex flex-col gap-1">
          <StatusPill tone={componentTone(c)}>{componentLabel(c)}</StatusPill>
          {c.security_note && <span className="text-[11.5px] text-crit">{c.security_note}</span>}
          {c.error && <span className="text-[11.5px] text-muted">{c.error}</span>}
        </span>
      ),
    },
    {
      key: "notes_url",
      label: "Release notes",
      value: (c) => c.notes_url || "",
      render: (c) => (c.notes_url ? <a className="text-accent underline" href={c.notes_url} target="_blank" rel="noreferrer">notes</a> : "—"),
    },
  ];
  return (
    <Section
      title="Components we carry"
      className="mt-6"
      hint={res ? `read ${formatRelative(res.checked_at)} on ${res.checked_on}` : "never checked on this node"}
      actions={
        <Button size="sm" variant="accent" disabled={check.isPending} onClick={() => check.mutate()}>
          {check.isPending ? "Checking…" : "Check for updates"}
        </Button>
      }
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="the last component check">
        {res ? (
          <DataList items={res.components} columns={cols} getRowKey={(c) => c.name} emptyMessage="" />
        ) : (
          <p className="text-[12.5px] text-muted">
            Nothing has asked upstream yet. Press <b>Check for updates</b>: each upstream is read once, now, and the
            answer is kept until the next press.
          </p>
        )}
      </StateView>
    </Section>
  );
}

interface Pending {
  name: string;
  from?: string;
  to: string;
  origins: string[];
  security: boolean;
  new?: boolean;
}
interface Refusal {
  package: string;
  reason: string;
}
interface HoldState {
  package: string;
  component: string;
  why: string;
  installed: boolean;
  held: boolean;
  ok: boolean;
}
interface RunState {
  run_id?: string;
  state: string;
  error?: string;
  finished_at?: string;
  reboot_required: boolean;
}
export interface OsReport {
  node: string;
  measured_at: string;
  pending: Pending[];
  security_count: number;
  kept_back?: string[];
  summary?: string;
  refusals?: Refusal[];
  error?: string;
  oldest_security_since?: string;
  holds?: HoldState[];
  holds_error?: string;
  lists_refreshed_at?: string;
  lists_basis?: string;
  reboot_required: boolean;
  reboot_packages?: string[];
  upgrade: RunState;
}
interface NodeReport {
  node: string;
  report?: OsReport;
  error?: string;
}
interface OsView {
  nodes: NodeReport[];
  self: string;
  running_job?: string;
  security_wait_days: number;
  lists_age_days: number;
}

const days = (iso?: string) => (iso ? (Date.now() - new Date(iso).getTime()) / 86400000 : NaN);

// The verdict for one node's row: could not check is never "nothing pending".
export function osVerdict(n: NodeReport): { tone: "ok" | "warn" | "bad" | "neutral"; label: string } {
  const r = n.report;
  if (!r) return { tone: "neutral", label: "could not ask" };
  if (r.error) return { tone: "neutral", label: "could not check" };
  if ((r.refusals?.length ?? 0) > 0) return { tone: "bad", label: "refused" };
  if (r.security_count > 0) return { tone: "bad", label: `${r.security_count} security` };
  if (r.pending.length > 0) return { tone: "warn", label: `${r.pending.length} pending` };
  return { tone: "ok", label: "nothing pending" };
}

export function OsUpdates() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<string[] | null>(null);
  const q = useQuery({
    queryKey: ["updates-os"],
    queryFn: () => client.apiFetch<OsView>("/updates/os"),
    refetchInterval: 30000,
  });
  const refresh = useMutation({
    mutationFn: (node: string) => client.apiFetch("/updates/os/refresh", { method: "POST", body: JSON.stringify({ node }) }),
    onSuccess: (_r, node) => {
      notify({ kind: "success", title: `Package lists refreshed on ${node}` });
      void qc.invalidateQueries({ queryKey: ["updates-os"] });
    },
    onError: (e) => notify({ kind: "error", title: "Refresh failed", body: e instanceof Error ? e.message : String(e) }),
  });
  const apply = useMutation({
    mutationFn: (nodes: string[]) =>
      client.apiFetch<{ job_id: string }>("/updates/os/apply", { method: "POST", body: JSON.stringify({ nodes }) }),
    onSuccess: (r) => {
      notify({ kind: "success", title: "Upgrade started", body: `Job ${r.job_id} — follow it on the Jobs screen.` });
      void qc.invalidateQueries({ queryKey: ["updates-os"] });
    },
    onError: (e) => notify({ kind: "error", title: "Not started", body: e instanceof Error ? e.message : String(e) }),
  });
  const d = q.data;
  // Members first, the node serving this console last.
  const allNodes = (d?.nodes ?? []).map((n) => n.node);
  const order = d ? [...allNodes.filter((n) => n !== d.self), ...allNodes.filter((n) => n === d.self)] : [];
  const busy = !!d?.running_job || apply.isPending;

  const cols: DataListColumn<NodeReport>[] = [
    { key: "node", label: "Node", sortable: true },
    {
      key: "verdict",
      label: "Pending",
      value: (n) => osVerdict(n).label,
      render: (n) => {
        const v = osVerdict(n);
        return (
          <span className="flex flex-col gap-1">
            <StatusPill tone={v.tone}>{v.label}</StatusPill>
            {(n.error || n.report?.error) && <span className="text-[11.5px] text-muted">{n.error || n.report?.error}</span>}
          </span>
        );
      },
    },
    {
      key: "security",
      label: "Oldest security update",
      value: (n) => n.report?.oldest_security_since || "",
      render: (n) => {
        const s = n.report?.oldest_security_since;
        if (!s) return "—";
        const late = d && days(s) > d.security_wait_days;
        return <span className={late ? "text-crit" : ""}>known {formatRelative(s)}</span>;
      },
    },
    {
      key: "lists",
      label: "Package lists",
      value: (n) => n.report?.lists_refreshed_at || "",
      render: (n) => {
        const at = n.report?.lists_refreshed_at;
        if (!at) return <span className="text-muted">age unknown</span>;
        const old = d && days(at) > d.lists_age_days;
        return <span className={old ? "text-warn" : ""} title={n.report?.lists_basis}>refreshed {formatRelative(at)}</span>;
      },
    },
    {
      key: "holds",
      label: "Carried packages",
      value: (n) => String(n.report?.holds?.filter((h) => !h.ok).length ?? ""),
      render: (n) => {
        const r = n.report;
        if (!r) return "—";
        if (r.holds_error) return <span className="text-muted">{r.holds_error}</span>;
        const bad = (r.holds ?? []).filter((h) => !h.ok);
        const held = (r.holds ?? []).filter((h) => h.installed && h.held).map((h) => h.package);
        return bad.length ? (
          <StatusPill tone="bad">not held: {bad.map((h) => h.package).join(", ")}</StatusPill>
        ) : (
          <span className="text-[12px]">held: {held.join(", ") || "none installed"}</span>
        );
      },
    },
    {
      key: "upgrade",
      label: "Last upgrade",
      value: (n) => n.report?.upgrade.state || "",
      render: (n) => {
        const u = n.report?.upgrade;
        if (!u || u.state === "none") return "—";
        return (
          <span className="flex flex-col gap-1">
            <StatusPill tone={u.state === "done" ? "ok" : u.state === "running" || u.state === "starting" ? "warn" : "bad"}>{u.state}</StatusPill>
            {n.report?.reboot_required && <span className="text-[11.5px] text-warn">reboot required</span>}
          </span>
        );
      },
    },
  ];

  const openReport = d?.nodes.find((n) => n.node === open)?.report;
  return (
    <Section
      title="Operating system updates"
      className="mt-6"
      hint={d?.running_job ? "an upgrade is running" : "from each node's own apt"}
      actions={
        <Button size="sm" variant="accent" disabled={busy || order.length === 0} onClick={() => setConfirm(order)}>
          Apply to every node
        </Button>
      }
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="OS updates">
        {d && (
          <>
            {d.running_job && (
              <Panel className="mb-3 p-3 text-[12.5px]">
                An upgrade is running — <Link className="text-accent underline" to="/jobs">follow it on the Jobs screen</Link>.
              </Panel>
            )}
            <DataList
              items={d.nodes}
              columns={cols}
              getRowKey={(n) => n.node}
              emptyMessage="No nodes."
              rowActions={(n) => (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setOpen(open === n.node ? null : n.node)}>
                    {open === n.node ? "Close" : "Packages"}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={refresh.isPending || busy} onClick={() => refresh.mutate(n.node)}>
                    {refresh.isPending && refresh.variables === n.node ? "Refreshing…" : "Refresh lists"}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy || !n.report || !!n.report.error} onClick={() => setConfirm([n.node])}>
                    Apply
                  </Button>
                </div>
              )}
            />
            {open && openReport && (
              <div className="mt-4">
                <h4 className="mb-2 text-[13px] font-semibold">{open}: {openReport.summary || "pending packages"}</h4>
                {(openReport.refusals?.length ?? 0) > 0 && (
                  <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px]">
                    <b>Refused:</b>
                    <ul className="ml-4 list-disc">{openReport.refusals!.map((r) => <li key={r.package}>{r.reason}</li>)}</ul>
                  </Panel>
                )}
                <DataList
                  items={openReport.pending}
                  getRowKey={(p) => p.name}
                  emptyMessage="Nothing pending."
                  columns={[
                    { key: "name", label: "Package", sortable: true },
                    { key: "from", label: "From", render: (p) => <code className="font-mono text-[11.5px]">{p.from || "(new)"}</code> },
                    { key: "to", label: "To", render: (p) => <code className="font-mono text-[11.5px]">{p.to}</code> },
                    { key: "origins", label: "Origin", value: (p) => p.origins.join(", "), render: (p) => <span className="text-[11.5px]">{p.origins.join(", ")}</span> },
                    { key: "security", label: "", value: (p) => (p.security ? "security" : ""), render: (p) => (p.security ? <StatusPill tone="bad">security</StatusPill> : null) },
                  ]}
                />
                {(openReport.kept_back?.length ?? 0) > 0 && (
                  <p className="mt-2 text-[12px] text-muted">Kept back (held): {openReport.kept_back!.join(", ")}</p>
                )}
              </div>
            )}
          </>
        )}
      </StateView>
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm && confirm.length === 1 ? `Upgrade ${confirm[0]}?` : "Upgrade every node, one at a time?"}
        destructive
        confirmLabel="Upgrade"
        pending={apply.isPending}
        onConfirm={() => {
          const nodes = confirm;
          setConfirm(null);
          if (nodes) apply.mutate(nodes);
        }}
        body={
          <div className="space-y-2">
            <p>Order: <b>{(confirm ?? []).join(" → ")}</b>.</p>
            <p>
              Each node is upgraded on its own, then measured until it is back as it was — Galera Synced, every role where it
              was, the mail and web units active, the service modules healthy. Only then does the next one start. A failure
              stops the run and says why; the nodes after it are not touched.
            </p>
            <p>Packages we carry stay held and are never installed. A node that asks for a reboot is NOT rebooted.</p>
          </div>
        }
      />
    </Section>
  );
}
